import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { useAuthentication } from 'gw-digital-auth-react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { ActivitiesService } from 'gw-capability-gateway';
import { ModalNextProvider } from '@jutro/components';
import { messages as commonMessages } from 'gw-platform-translations';
import metadata from './ReassignActivity.metadata.json5';
import styles from './ReassignActivity.module.scss';
import messages from './ReassignActivity.messages';

const ReassignActivity = ({ activity, onUpdateActivity }) => {
    const { authHeader } = useAuthentication();
    const [selectedValue, setSelectedValue] = useState('');
    const [editable, setIsEditable] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const activityWrapperStyle = classNames(styles.gwReassignActivityWrapper, {
        [styles.gwReassignActivityDisplay]: activity.canReassign
    });
    const enableEditing = useCallback(() => {
        if (activity.canReassign) {
            setIsEditable(true);
        }
    }, [activity]);
    const writeValue = useCallback(
        (value) => {
            setSelectedValue(value);
        },
        [setSelectedValue]
    );
    const getAvailableAssignableUser = useCallback((activities) => {
        return (
            activities.assignableUsers
            && activities.assignableUsers.map((availableUser) => {
                return {
                    code: availableUser,
                    name: availableUser
                };
            })
        );
    }, []);
    const resetAfterReassign = useCallback(() => {
        setLoading(false);
        setSelectedValue('');
        setIsEditable(false);
    }, [setLoading, setSelectedValue, setIsEditable]);
    const assignToSelectedUser = useCallback(async () => {
        setLoading(true);
        ActivitiesService.reassignActivity(selectedValue, activity.publicID, authHeader)
            .then((response) => {
                onUpdateActivity(response);
                resetAfterReassign();
            })
            .catch((error) => {
                resetAfterReassign();
                const errorMessage = _.get(error, 'baseError.data.error.message');
                if (errorMessage) {
                    ModalNextProvider.showAlert({
                        title: messages.unableToReassign,
                        message: error.baseError.data.error.message,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                }
            });
    }, [onUpdateActivity, resetAfterReassign, selectedValue, authHeader, activity]);
    const disableEditing = useCallback(() => {
        setSelectedValue('');
        setIsEditable(false);
    }, [setIsEditable, setSelectedValue]);

    const overrideProps = {
        assignableUserList: {
            availableValues: getAvailableAssignableUser(activity),
            value: selectedValue
        },
        reassignActivityWrapper: {
            visible: !editable,
            className: activityWrapperStyle
        },
        selectUserContainer: {
            visible: editable
        },
        assignedName: {
            content: _.get(activity, 'assignedTo.displayName')
        },
        reassignPageLoader: {
            loaded: !isLoading
        },
        activityReassignContainer: {
            visible: !isLoading
        }
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            handleValueChange: writeValue,
            enableEditing: enableEditing,
            disableEditing: disableEditing,
            assignToSelectedUser: assignToSelectedUser,
            onClickPrevent: (e) => e.stopPropagation()
        }
    };
    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
};

ReassignActivity.propTypes = {
    activity: PropTypes.shape({}).isRequired,
    onUpdateActivity: PropTypes.func
};

export default ReassignActivity;
